import { useEffect, useRef, useState } from "react";
import { css } from "styled-components/macro";
import { VideoElementAudio, VideoElementState } from "../../types/Video";
import { ReactComponent as AudioPlayIcon } from "../../assets/icons/Play.svg";
import { ReactComponent as AudioPauseIcon } from "../../assets/icons/AudioPause.svg";
import { CircularProgressBar } from "../../components/CircularProgressBar";
import { usePlayback } from "../../../VideoEditor/contexts/PlaybackContext";
import recordingAudio from "../../assets/images/recording-audio.png";

export function ElementAudioRenderer(props: {
  element: VideoElementAudio;
  state: VideoElementState;
  controlled?: boolean;
}) {
  const ref = useRef<HTMLAudioElement>(null);
  const animationRef = useRef<any>(null);
  const [playing, setPlaying] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const { currentFrame, playing: playbackPlaying, pause } = usePlayback();
  const audioFiles = currentFrame.filter((elem) => elem.type === "audio");
  const firstAudioId = audioFiles.sort((a, b) => {
    const isCloserToTheEdge =
      a.states[0].left * a.states[0].top > b.states[0].left * b.states[0].top;

    if (isCloserToTheEdge) {
      return 1;
    }
    return -1;
  })[0];

  useEffect(() => {
    const currentRef = ref.current;
    if (currentRef) {
      if (playing) currentRef.play();
      else if (playbackPlaying) {
        if (audioFiles.length > 1 && firstAudioId.id === props.element.id) {
          currentRef.play();
        } else if (audioFiles.length === 1) {
          currentRef.play();
        }
      } else {
        currentRef.pause();
      }
    }

    const updateDuration = () => {
      if (currentRef) {
        if (currentRef.ended) {
          setCurrentProgress(props.element.file.duration);
        } else {
          setCurrentProgress(currentRef.currentTime);
        }
      }

      animationRef.current = window.requestAnimationFrame(updateDuration);
    };

    const onEnded = () => {
      if (audioFiles.length > 1) {
        pause();
      }
      setCurrentProgress(props.element.file.duration);
      setPlaying(false);
    };

    if (playing) {
      animationRef.current = window.requestAnimationFrame(updateDuration);
    }

    if (currentRef) {
      currentRef.addEventListener("ended", onEnded);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("ended", onEnded);
      }

      window.cancelAnimationFrame(animationRef.current);
    };
  }, [
    playing,
    props.element.file.duration,
    playbackPlaying,
    audioFiles.length,
    firstAudioId.id,
    pause,
    props.element.id,
  ]);

  return (
    <div
      css={css`
        width: 100%;
        height: 100%;
        text-align: center;

        img {
          width: 100%;
          height: auto;
          user-select: none;
        }
      `}
    >
      <audio controls={false} ref={ref}>
        <source src={props.element.file.file} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <button
        css={css`
          position: relative;
          width: ${props.state.width}px;
          height: ${props.state.width}px;
          margin: 0 auto;
          border-radius: 50%;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: #fff url(${recordingAudio}) no-repeat center center;
          background-size: 70% auto;
          overflow: hidden;

          &:hover {
            .overlay {
              opacity: 1;
              visibility: visible;
            }
          }
        `}
        onClick={() => {
          setPlaying(!playing);
        }}
      >
        <CircularProgressBar
          totalDuration={props.element.file.duration}
          progress={currentProgress}
          strokeWidth={0.5}
          playing={playing}
          css={css`
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
          `}
        />
        <div
          css={css`
            position: absolute;
            width: calc(100% - 15px);
            height: calc(100% - 15px);
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            margin: auto;
            border-radius: 50%;
            border: 1px solid #eae8e8;
          `}
        >
          <div
            className="overlay"
            css={css`
              position: absolute;
              width: 100%;
              height: 100%;
              top: 0;
              left: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(37, 36, 36, 0.7);
              border-radius: 50%;
              opacity: 0;
              visibility: hidden;
              transition: opacity 0.3s ease-in-out;
            `}
          >
            {playing ? (
              <AudioPauseIcon width="22%" fill={"#ffffff"} />
            ) : (
              <AudioPlayIcon width="22%" height="auto" fill={"#ffffff"} />
            )}
          </div>
        </div>
      </button>
    </div>
  );
}
